import spacetime from "spacetime";
import CalendarDateModel from "./CalendarDateModel.js";
import CalendarDate from "../Components/CalendarDate.vue";
import { merge } from "lodash";
import { today, filterOutNonConfigSettings } from "../helpers.js";
import SelectedDatesModel from "./SelectedDatesModel.js";


export default class CalendarConfigModel {

    /**
     * @type {number}
     */
    calendarIndex = 0;

    /**
     * @param {CalendarConfigModel} config
     */
    constructor(config)
    {
        filterOutNonConfigSettings(this, config);
        merge(this, config);
        let elements = [];
        for (const el of document.querySelectorAll(this.elements).values()) {
            elements.push(el);
        }
        this.elements = elements;
        this.setOrderOfDaysBasedOnWeekStartDay();
        this.startDate = this.correctDateInPastToCurrentDate(this.startDate, this.displayMonthDatesOnly);
        this.minDisplayDate = this.correctDateInPastToCurrentDate(this.minDisplayDate);
        this.maxDisplayDate = this.correctDateInPastToCurrentDate(this.maxDisplayDate);
    }

    /**
     * @param {string} dateToCorrect
     * @param {boolean} startOfMonthOnly
     * @returns {string}
     */
    correctDateInPastToCurrentDate(dateToCorrect, startOfMonthOnly = false)
    {
        const todaySpacetime = today();
        let date = spacetime(dateToCorrect);

        if (! startOfMonthOnly && date.isBefore(todaySpacetime)) {
            date = todaySpacetime;
        }
        else if (startOfMonthOnly && date.isBefore(todaySpacetime)) {
            date = todaySpacetime.startOf('month');
        }

        return date.format(CalendarDateModel.DATE_FORMAT);
    }

    /**
     * @type {string}
     */
    calendarRequestUrl = window.location.href;

    /**
     * @type {string}
     */
    availabilityUrl = window.location.origin + '/rate-rooms1';

    /**
     * @type {HTMLElement|string|null}
     */
    elements = null;
    /**
     * If your building a calendar that needs to update when the booking widget has changes
     * you can set the BookingWidgetConfig object here
     * @type {Object|null}
     */
    bookingWidgetConfig = null;

    /**
     * When enabled the calendar will only not request pricing
     * @type {boolean}
     */
    inventoryOnly = false;

    /**
     * When using multi DetailCalendars, each date range must start where the previous began
     *
     * Eg. Calendar 1 (Selected Dates A -> D) - Calendar 2 (Selected Dates D -> G)
     * @type {boolean}
     */
    enableMultiDestinationCalendar = false;

    /**
     * @type {string}
     */
    type = 'Calendar';
    /**
     * @type {{Calendar: string, DetailCalendar: string}}
     */
    static supportedTypes = {
        Calendar: 'Calendar',
        DetailCalendar: 'DetailCalendar'
    };
    /**
     * @type {boolean}
     */
    showCalendar = true;
    /**
     * Date the calendar go to
     * @type {string}
     */
    startDate = today().format(CalendarDateModel.DATE_FORMAT);
    /**
     * Day the calendar week should start on
     * @type {string}
     */
    weekStartDay = 'Monday';
    /**
     * Flexible date which can be highlighted
     * Must be in iso-date format if set
     * @type {string|null}
     */
    highlightDate = null;
    /**
     * Number of dates cells to display on the Calendar
     * @type {number}
     */
    daysToDisplay = 28;
    /**
     * When enabled the calendar will only display date for each month.
     * This will ignore the "daysToDisplay" config
     * @type {boolean}
     */
    displayMonthDatesOnly = false;
    /**
     * Text for days of the week
     * (Must contain 7 days)
     *
     * @note This will reorder depending on the set "weekStartDay" config value
     */
    days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    /**
     * Spacetime translations
     * @see https://github.com/spencermountain/spacetime#custom-languages
     * @type {object}
     * @note Week day names must begin with Sunday and end with Saturday for spacetime.js
     */
    translations = {
        arrival: 'Arrival',
        departure: 'Departure',
        days: {
            short: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
            long: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
        },
        months: {
            short: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
            long: ['January','February','March','April','May','June','July','August','September','October','November','December']
        }
    };
    /**
     * Used to control the order of week days display on the calendar
     * @note This will reorder depending on the set "weekStartDay" config value
     * @private
     */
    _weekDaysList = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    /**
     * @param dayName {string}
     * @param spacetimeWeekDayOrder {boolean}
     * @return {number}
     */
    getDayNameIndex(dayName, spacetimeWeekDayOrder = false)
    {
        let list = this._weekDaysList;
        if (spacetimeWeekDayOrder) {
            list = this.translations.days.long.map(d => d.toLowerCase());
        }
        return list.indexOf(dayName.toLowerCase());
    }
    /**
     * Re-orders days of week based on the index passed.
     *
     * @param days {string[]} Must always be Mon-Sun
     * @param index {number}
     * @return {string[]}
     */
    orderDaysStartingWithIndex(days, index)
    {
        const sortedDays = days.slice(0, index);
        const restOfDays = days.slice(sortedDays.length, days.length);
        return [...restOfDays, ...sortedDays];
    }
    /**
     * Re-orders arrays contain the week day names to match the desired weekStartDay
     */
    setOrderOfDaysBasedOnWeekStartDay()
    {
        const weekDayIndex = this.getDayNameIndex(this.weekStartDay);
        this.days = this.orderDaysStartingWithIndex(this.days, weekDayIndex);
        this._weekDaysList = this.orderDaysStartingWithIndex(this._weekDaysList, weekDayIndex);
    }
    /**
     * If enabled, requests will be chunked by 7 days
     * otherwise all dates will be requested at once
     * @type {boolean}
     */
    chunkRequests = false;
    /**
     * If enabled, a range of dates will be selectable
     * otherwise a link to that date will render
     * @type {boolean}
     */
    rangeMode = false;
    /**
     * Shows the days of the week
     * @type {boolean}
     */
    showDaysOfWeek = true;
    /**
     * When enabled the calendar behaves as a basic calendar where no prices are shown
     * @type {boolean}
     */
    enabledBasicCalendar = false;
    /**
     * Apply restrictions to days
     *
     * @type {RestrictionSettingsModel[]}
     */
    restrictions = [];
    /**
     * Number of decimal places to show on price
     * @type {number}
     */
    decimalsPlaces = 2;
    /**
     * The HTML to render inside the forward button
     * @type {string}
     */
    forwardHTML = `
    <i class="fa-solid fa-angle-right calendar-hide-mobile"></i>
    <i class="fa-solid fa-angle-down calendar-hide-desktop"></i>`;
    /**
     * The HTML to render inside the back button
     * @type {string}
     */
    backHTML = `
    <i class="fa-solid fa-angle-left calendar-hide-mobile"></i>
    <i class="fa-solid fa-angle-up calendar-hide-desktop"></i>`;
    /**
     * The minimum date that can be displayed/selected
     * @type {string}
     */
    minDisplayDate = (() => {
        return today().format(CalendarDateModel.DATE_FORMAT);
    })();
    /**
     * The maximum date that can be displayed/selected
     * @type {string}
     */
    maxDisplayDate = (() => {
        return today().add(2, 'year').format(CalendarDateModel.DATE_FORMAT);
    })();
    /**
     * @type {string[]}
     */
    disableDates = [];
    /**
     * Used to close today's date after the given time.
     *
     * E.g If you don't want today's date to be selected after 10pm then set the
     * time to "22:00"
     *
     * @type {{timezone: string, time: string}}
     */
    closeoutToday = (() => {
        const now = spacetime();
        return {
            time: '23:59',
            timezone: now.timezone().name
        }
    })();
    /**
     * This method tells the Calendar which component it should use to render
     * in each date cell, this allows for completely custom HTML in the cells per client
     */
    getCalendarDateComponent()
    {
        return CalendarDate;
    }
    /**
     * Returns a copy of the CalendarConfigModel, useful to list the available config options
     *
     * @return {CalendarConfigModel}
     */
    static getConfig()
    {
        return Object.assign({}, CalendarConfigModel);
    }
    /**
     * @param calendarDateModel {CalendarDateModel}
     */
    getMessagesToDisplay(calendarDateModel)
    {
        let messages = [];
        if (! calendarDateModel.isAvailable()) {
            messages.push('<p><i class="fa-solid fa-xmark"></i> <small>Not Available</small></p>');
        }
        else if (calendarDateModel.isClosedForArrival() && calendarDateModel.isClosedForDeparture()) {
            messages.push('<small>No Arr/Dep</small>');
        }
        else if (calendarDateModel.isClosedForArrival()) {
            messages.push('<small>No Arrival</small>');
        }
        else if (calendarDateModel.isClosedForDeparture()) {
            messages.push('<small>Departure</small>');
        }
        else if (calendarDateModel.getMinLengthOfStay() > 0) {
            messages.push('<small>Must stay '+ calendarDateModel.getMinLengthOfStay() +' nights</small>')
        }
        if (calendarDateModel.isSelectedArrival()) {
            messages.push('<small>Arrival</small>');
        }
        else if (calendarDateModel.isSelectedDeparture()) {
            messages.push('<small>Depart</small>');
        }
        return messages.join(' ');
    }
    /**
     * Date format for the month/year in the title
     * @type {string}
     */
    calendarTitleFormat = '{month-short} {year}';
    /**
     * @param firstCalendarDate {spacetime}
     * @param lastCalendarDate {spacetime}
     * @return {string}
     */
    getCalendarTitleFormat(firstCalendarDate, lastCalendarDate)
    {
        const titlePartOne = firstCalendarDate.format(this.calendarTitleFormat);
        const titlePartTwo = lastCalendarDate.format(this.calendarTitleFormat);
        let title = titlePartOne;
        if (titlePartOne !== titlePartTwo) {
            title += ` / ${titlePartTwo}`;
        }
        return `<p>${title}</p>`;
    }

    /**
     * @param monthStartDate {spacetime}
     * @returns {string}
     */
    getCalendarMonthTitle(monthStartDate)
    {
        return `<p>${monthStartDate.format('{month} {year}')}</p>`;
    }

    /**
     * @type {string}
     */
    currency = '&euro;';
    /**
     * Date formats to display on different screen sizes
     *
     * @type {{desktop: string, mobile: string}}
     */
    dateFormatsForScreenSizes = {
        desktop: '{date-ordinal} {month-short}',
        mobile: '{day-short} {date-ordinal} {month-short}'
    };
    /**
     * Returns the date format to  display based on the screen size
     *
     * @return {string}
     */
    getDateFormat()
    {
        let format = this.dateFormatsForScreenSizes.desktop;
        if (this._windowWidth <= this.mobileBreakPoint) {
            format = this.dateFormatsForScreenSizes.mobile;
        }
        return `<p>${format}</p>`;
    }
    /**
     * @type {number}
     */
    mobileBreakPoint = 992;
    /**
     * @type {Ref<UnwrapRef<number>>}
     * @private
     */
    _windowWidth = 0;
    /**
     * When moving calendar forward on mobile devices, the window will refocus to the top
     * of the calendar. This value will offset the scroll to position to make the animation
     * look more natural
     *
     * @type {number}
     */
    mobileScrollTopOffset = 100;
    /**
     * Used to add functionality to the DetailCalendar
     *
     * @link https://vuejs.org/guide/reusability/composables.html#what-is-a-composable
     * @type {function[]}
     */
    composables = [];
}
